import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { guardLeave } from '../../utils/unsaved';

// ── THE BOTTOM-LEFT CORNER ──────────────────────────────────────────────────────────────────────
// Two chips, one corner, two different problems that look the same from the glass: a login that
// cannot sign out (PinnedSession) and a page that cannot get home (BackToModules). They never
// appear together — a pinned role never reaches the staff branch of AuthGate — so they share the
// corner and the shape, and each says plainly which question it answers.

/** The pill both chips are drawn as. Bottom LEFT on purpose: every other fixed thing in this app —
 *  the Fab, the toasts, the upload indicator, the new-mail pop-up, Joy — is bottom right, and the
 *  busy top right of these pages belongs to the accounting-period selector and Classic view. */
const pill: React.CSSProperties = {
  position: 'fixed', left: 16, bottom: 16, zIndex: 3000,
  display: 'flex', alignItems: 'center', gap: 8,
  maxWidth: 'calc(100vw - 32px)', padding: '6px 10px 6px 11px',
  borderRadius: 999, border: '1px solid var(--border)',
  background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)',
};

const chipBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
  padding: '3px 6px', borderRadius: 7, border: 'none', background: 'transparent',
  color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

// ── THE WAY OUT OF A PINNED LOGIN (owner 2026-10-20) ────────────────────────────────────────────
//
// ⛔ SIGN OUT LIVES ON '/', AND A PINNED LOGIN CAN NEVER GO THERE. The module pages under /m/ carry
// no chrome of their own — no sidebar, no top bar — because staff reach them from the module home
// and sign out there. Three logins are held away from that home by a gate: the auditor (SLF Tax
// only), the books login (ONE client's books only), and Joy's kiosk. Joy's kiosk has always had its
// own Sign out in the corner. The other two had none at all: once signed in, there was no way to
// sign out, on any screen, ever.
//
// The clearest evidence it was an oversight rather than a decision is in BooksGate itself, which
// already renders a Sign out — but only on the branch where the login has NO client pinned. Get the
// configuration right and the button disappears.
//
// ⛔ SO IT BELONGS TO THE GATE, NOT TO THE SCREENS. The two screens a books login sees today are the
// company workspace and the statutory books; putting a button on each of them would fix today's
// symptom, leave /m/tax exactly as it is, and leave the next pinned role to discover the same hole.
// One component, rendered beside each gate's Outlet, covers every screen those roles can reach —
// including the ones not built yet.
//
// Bottom LEFT on purpose. Both of those screens have a busy top right (the accounting-period
// selector and Classic view on the workspace), and every other fixed thing in this app — the Fab,
// the toasts, the upload indicator, the new-mail pop-up, Joy — sits bottom right. The bottom left
// corner is the one piece of chrome nothing else wants.
//
// It names the ACCOUNT, not just the action. A books login is the customer's own seat (and the
// firm's staff may be looking at the same screen over their shoulder), so "who am I signed in as"
// is the question the chip exists to answer; the client's own name is already in the page heading.
export default function PinnedSession() {
  const { t } = useTranslation();
  const { me, logout } = useStore();
  const who = String(me?.email || '').trim();
  return (
    <div style={pill}>
      <i className="ti ti-user-circle" style={{ fontSize: 15, color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
      {/* A long address is cut rather than allowed to push the button off the screen; the whole of
          it stays readable on hover and to a screen reader. */}
      {who && (
        <span title={who} style={{
          fontSize: 12, color: 'var(--text-tertiary)', maxWidth: 220,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{who}</span>
      )}
      {/* ⛔ The address is TEXT and only the word is a button: on a customer's own seat, a chip that
          signs you out wherever you happen to click it is a trap, not a convenience. */}
      <span style={{ width: 1, height: 14, background: 'var(--border)', flexShrink: 0 }} aria-hidden />
      {/* Signing out takes the page — and any window open on it — away. With something typed in one,
          the shells' own question is asked first (utils/unsaved.ts); with nothing, it signs out at once. */}
      <button onClick={() => guardLeave(logout)} title={t('signOut')} style={chipBtn}>
        <i className="ti ti-logout" style={{ fontSize: 14 }} aria-hidden />{t('signOut')}
      </button>
    </div>
  );
}

// ── THE WAY HOME FROM A MODULE PAGE (owner 2026-10-20) ──────────────────────────────────────────
//
// Staff are not pinned: Sign out is on '/', and they can reach it. What they could not do is GET
// there — the pages under /m/ carry no chrome, so the only way back to the module home was the
// browser's own Back button or editing the address. So this is a way home, NOT a second Sign out:
// duplicating the sign-out onto thirty-odd module pages would scatter the one action that ends a
// session, and the owner's call (2026-10-20) was the way home instead.
//
// ⛔ IT SELF-GATES ON THE PATH, mounted once beside the other self-gating overlays in AuthGate,
// because there is no single wrapper to hang it on: RequireModule covers 30 of the /m/ routes but
// not /m/version, /m/bugify, /m/client_dashboard, the two RequireAny container pages, or the
// /m/:key stub — so hanging it there would leave six pages behind and nobody would notice which.
export function BackToModules() {
  const { t } = useTranslation();
  const loc = useLocation();
  const navigate = useNavigate();
  if (!loc.pathname.startsWith('/m/')) return null;
  return (
    <div style={pill}>
      {/* The way home unmounts the module page and any window open on it: asked first when something
          is typed in one (utils/unsaved.ts), straight through otherwise. */}
      <button onClick={() => guardLeave(() => navigate('/'))} title={t('back_to_portal')} style={chipBtn}>
        <i className="ti ti-arrow-left" style={{ fontSize: 14 }} aria-hidden />{t('back_to_portal')}
      </button>
    </div>
  );
}
